import React from 'react';
import { Link } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { PRIMARY } from '../../components/LmsSectionCard';
import LmsNewsPhoto from '../components/LmsNewsPhoto';
import LmsNewsBadges from '../components/LmsNewsBadges';
import useLmsNewsDetailData from '../hooks/useLmsNewsDetailData';
import { formatFee, formatLocalDateTime, formatTrainingDateRange, getNewsTitle, isAnnouncement } from '../utils/newsUtils';

const NEWS_LIST_PATH = '/learning-management-system/news';

const panelStyle = { border: '1px solid #EAECF0', backgroundColor: '#FFFFFF', borderRadius: '1rem', boxShadow: '0 1px 2px rgba(16,24,40,0.04)' };

const MetaItem = ({ icon, text }) => (
    <span className="flex align-items-center gap-2 text-sm" style={{ color: '#344054' }}>
        <i className={`pi ${icon} text-color-secondary`} />
        {text}
    </span>
);

const Section = ({ icon, title, children, showDivider }) => (
    <div className="py-4" style={showDivider ? { borderTop: '1px solid #EAECF0' } : undefined}>
        <div className="flex align-items-center gap-3 mb-2">
            <i className={`pi ${icon}`} style={{ fontSize: '1.1rem', color: '#344054' }} />
            <span className="font-semibold" style={{ fontSize: '1.05rem', color: '#101828' }}>
                {title}
            </span>
        </div>
        <div className="pl-5">{children}</div>
    </div>
);

const InfoRow = ({ icon, label, value }) => (
    <div className="flex align-items-start gap-3 py-2">
        <i className={`pi ${icon} text-color-secondary mt-1`} />
        <span className="text-color-secondary" style={{ width: '7.5rem', flexShrink: 0 }}>
            {label}
        </span>
        <span className="font-medium" style={{ color: '#101828', wordBreak: 'break-word' }}>
            {value || '-'}
        </span>
    </div>
);

const LmsNewsDetail = () => {
    const { th } = useLanguage();
    const { news, isLoading, notFound } = useLmsNewsDetailData();

    const backLink = (
        <Link to={NEWS_LIST_PATH} className="no-underline flex align-items-center gap-2 font-medium mb-3" style={{ color: PRIMARY, width: 'fit-content' }}>
            <i className="pi pi-arrow-left" />
            {th('lmsNews.backToNews')}
        </Link>
    );

    if (isLoading) {
        return (
            <div className="flex align-items-center justify-content-center" style={{ minHeight: '20rem' }}>
                <ProgressSpinner style={{ width: '3rem', height: '3rem' }} strokeWidth="4" />
            </div>
        );
    }

    if (notFound) {
        return (
            <div>
                {backLink}
                <div className="flex flex-column align-items-center gap-2 p-6 text-color-secondary" style={panelStyle}>
                    <i className="pi pi-exclamation-circle" style={{ fontSize: '2rem' }} />
                    <span>{th('lmsNews.notFound')}</span>
                </div>
            </div>
        );
    }

    const announcement = isAnnouncement(news);
    const preconditions = Array.isArray(news.preconditions) ? news.preconditions : [];
    const dateRange = formatTrainingDateRange(news);

    return (
        <div>
            {backLink}

            <div className="grid">
                <div className={announcement ? 'col-12' : 'col-12 lg:col-8'}>
                    <div className="flex flex-column md:flex-row overflow-hidden mb-3" style={panelStyle}>
                        <div className="flex flex-column gap-3 p-4 flex-1" style={{ minWidth: 0 }}>
                            <div className="flex align-items-center gap-3 flex-wrap">
                                <h1 className="m-0" style={{ fontSize: '1.6rem', color: '#101828', lineHeight: 1.25 }}>
                                    {getNewsTitle(news)}
                                </h1>
                                <LmsNewsBadges news={news} />
                            </div>

                            {news.shortDescription && (
                                <p className="m-0" style={{ color: '#475467', lineHeight: 1.55 }}>
                                    {news.shortDescription}
                                </p>
                            )}

                            <div className="flex align-items-center gap-4 flex-wrap mt-auto">
                                {announcement ? (
                                    news.publishDate && <MetaItem icon="pi-calendar" text={formatLocalDateTime(news.publishDate)} />
                                ) : (
                                    <>
                                        {dateRange && <MetaItem icon="pi-calendar" text={dateRange} />}
                                        {news.planningLocationText && <MetaItem icon="pi-map-marker" text={news.planningLocationText} />}
                                        {news.trainingCapacity != null && <MetaItem icon="pi-users" text={th('lmsNews.participants', { count: news.trainingCapacity })} />}
                                        {news.trainingTypeText && <MetaItem icon="pi-tag" text={news.trainingTypeText} />}
                                    </>
                                )}
                            </div>
                        </div>

                        <LmsNewsPhoto news={news} className="w-full md:w-5" style={{ minHeight: '14rem' }} />
                    </div>

                    <div className="px-4" style={panelStyle}>
                        <div className="pt-3">
                            <span className="inline-block font-semibold pb-2" style={{ color: PRIMARY, borderBottom: `2px solid ${PRIMARY}` }}>
                                {th('lmsNews.overview')}
                            </span>
                        </div>

                        <Section icon="pi-file" title={announcement ? th('lmsNews.aboutAnnouncement') : th('lmsNews.aboutTraining')} showDivider>
                            <p className="m-0" style={{ color: '#475467', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                                {news.detailDescription || news.shortDescription || '-'}
                            </p>
                        </Section>

                        {!announcement && preconditions.length > 0 && (
                            <Section icon="pi-check-square" title={th('lmsNews.prerequisites')} showDivider>
                                <ul className="m-0 p-0 list-none flex flex-column gap-2">
                                    {preconditions.map((item) => (
                                        <li key={item.preconditionId ?? item.code} className="flex align-items-start gap-2">
                                            <i className="pi pi-check-circle mt-1" style={{ color: PRIMARY, fontSize: '0.9rem' }} />
                                            <span style={{ color: '#344054' }}>
                                                <span className="font-medium">{item.precondition || item.code}</span>
                                                {item.description && <span className="text-color-secondary"> — {item.description}</span>}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </Section>
                        )}
                    </div>
                </div>

                {!announcement && (
                    <div className="col-12 lg:col-4">
                        <div className="p-4" style={panelStyle}>
                            <div className="font-semibold mb-2" style={{ fontSize: '1.05rem', color: '#101828' }}>
                                {th('lmsNews.trainingInfo')}
                            </div>
                            <InfoRow icon="pi-calendar" label={th('lmsNews.fields.date')} value={dateRange} />
                            <InfoRow icon="pi-map-marker" label={th('lmsNews.fields.location')} value={news.planningLocationText} />
                            <InfoRow icon="pi-users" label={th('lmsNews.fields.capacity')} value={news.trainingCapacity != null ? th('lmsNews.participants', { count: news.trainingCapacity }) : ''} />
                            <InfoRow icon="pi-tag" label={th('lmsNews.fields.trainingType')} value={news.trainingTypeText} />
                            <InfoRow icon="pi-wallet" label={th('lmsNews.fields.price')} value={formatFee(news, th('lmsNews.free'))} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LmsNewsDetail;
